import React from 'react';
import { Pane } from 'react-leaflet';
import { Marker, MarkerLayer } from 'react-leaflet-marker';
import { useRecoilValue } from 'recoil';
import { TourPinV2 } from '@app/shared/components/tour-pin/TourPinV2';
import { Stop, Tour } from '@app/shared/models/tour.type';
import { PlaceDetails } from '@app/shared/state/user-added-stops.atom';
import { atom_visitedLocations } from '@app/shared/state/visited.atom';
import lighthouseSvg from './lighthouse-iso.svg';
import museumSvg from './museum-iso-v3.svg';

const svgStopMarkers: Record<string, { anchor: string, src: string, baseSize: number }> = {};

interface MapPinLayerProps {
  clusters: any[];
  individualMarkers: any[];
  clusteredUserPlaceIds: Set<string>;
  animatedMarkerIds: string[];
  animatedClusterIds: (string | number)[];
  currentZoom: number;
  activeStopId: string | undefined;
  isDemo: boolean;
  showClustersAndMarkers: boolean;
  activeStop: Stop | undefined;
  userAddedPlaces: PlaceDetails[];
  tourId: string;
  tourConfig: any;
  originalTourStops: Stop[] | undefined;
  currentStops: Stop[] | undefined;
  allRoutes: any[];
  getPinSize: (zoom: number) => number;
  getPinDrawer: (stop: Stop, groupIndex: number) => React.ReactNode;
  handleClusterClick: (cluster: any) => void;
  dropPinStep: string | null | undefined;
  dropPinTempPlaceId: string | null | undefined;
  isPlannedRouteResults: boolean;
  plannedRouteStopOrder: string[] | undefined;
  isDesktop: boolean;
  hasRouteToStop: boolean;
  themePrimaryColor: string;
}

export const MapPinLayer = React.memo(function MapPinLayer({
  clusters,
  individualMarkers,
  clusteredUserPlaceIds,
  animatedMarkerIds,
  animatedClusterIds,
  currentZoom,
  activeStopId,
  isDemo,
  showClustersAndMarkers,
  activeStop,
  userAddedPlaces,
  tourId,
  tourConfig,
  originalTourStops,
  currentStops,
  allRoutes,
  getPinSize,
  getPinDrawer,
  handleClusterClick,
  dropPinStep,
  dropPinTempPlaceId,
  isPlannedRouteResults,
  plannedRouteStopOrder,
  isDesktop,
  hasRouteToStop,
  themePrimaryColor,
}: MapPinLayerProps) {
  // Subscribe to visited locations directly (avoids NewMapContainer re-render)
  const visitedLocations = useRecoilValue(atom_visitedLocations);

  // If clusters/markers are hidden, show only the active stop
  if (!showClustersAndMarkers) {
    if (!activeStop) return null;
    return (
      <Pane name="tour-stops" style={{ zIndex: 610 }}>
        <MarkerLayer>
          <Marker
            key={activeStop.id}
            position={{
              lat: activeStop.stop_location.latitude,
              lng: activeStop.stop_location.longitude,
            }}
            placement="center"
            size={[0, 0]}
          >
            <div
              className="counter-rotate"
              style={{
                zIndex: 1000,
                pointerEvents: isDemo ? 'none' : 'auto',
                animation: 'markerPopIn 0.5s cubic-bezier(0.23, 1, 0.32, 1)'
              }}>
              <TourPinV2
                visited={visitedLocations?.[activeStop.id]}
                active={true}
                disabled={isDemo}
                size={getPinSize(currentZoom)}
                isDesktop={isDesktop}
                id={activeStop.id}
              >
                {activeStop.config?.pin ? (
                  <img src={activeStop.config.pin} alt="" aria-hidden="true" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  getPinDrawer(activeStop, 0)
                )}
              </TourPinV2>
            </div>
          </Marker>
        </MarkerLayer>
      </Pane>
    );
  }

  const tourStopColor = isDemo ? '#4a4a4a' : (tourConfig?.pinColor || themePrimaryColor);
  const userAddedColor = '#48526B';
  const clusterFontColor = tourConfig?.cluster_font || tourStopColor;

  const getClusterBorderStyle = (cluster: any, size: number) => {
    const userCount = cluster.userAddedCount || 0;
    const tourCount = cluster.tourStopCount || 0;

    if (userCount > 0 && tourCount === 0) {
      return {
        border: `2px solid ${userAddedColor}`,
        background: 'white',
        filter: `drop-shadow(0px 0px 2px ${userAddedColor})`,
        color: userAddedColor,
      };
    } else if (tourCount > 0 && userCount === 0) {
      return {
        border: `2px solid ${tourStopColor}`,
        background: 'white',
        filter: `drop-shadow(0px 0px 2px ${tourStopColor})`,
        color: clusterFontColor,
      };
    } else {
      return {
        border: `3px solid ${tourStopColor}`,
        boxShadow: `0 0 0 3px ${userAddedColor}, 0 0 4px 3px ${userAddedColor}`,
        background: 'white',
        color: clusterFontColor,
      };
    }
  };

  const currentStopIds = new Set((currentStops || []).map(s => s.id));
  const isFiltered = originalTourStops && originalTourStops.length > currentStopIds.size;
  const excludedRouteStops = (isFiltered && allRoutes.length > 0)
    ? originalTourStops.filter(stop =>
      stop.include_path !== false && !currentStopIds.has(stop.id)
    )
    : [];

  return (
    <Pane name="tour-stops" style={{ zIndex: 610 }}>
      <MarkerLayer>
        {/* Clusters */}
        {clusters.map((cluster, index) => {
          const clusterSize = isDesktop
            ? (currentZoom >= 17 ? 44 : currentZoom <= 14 ? 36 : 36 + ((currentZoom - 14) / (17 - 14)) * (44 - 36))
            : (currentZoom >= 17 ? 32 : currentZoom <= 14 ? 25 : 25 + ((currentZoom - 14) / (17 - 14)) * (32 - 25));
          const clusterTopOffset = clusterSize * -1;
          const clusterBorderStyle = getClusterBorderStyle(cluster, clusterSize);

          const isAnimated = !hasRouteToStop && animatedClusterIds.includes(cluster.id);
          return (
            <Marker
              key={`cluster-${cluster.id || index}`}
              position={{
                lat: cluster.geometry.coordinates[1],
                lng: cluster.geometry.coordinates[0],
              }}
              placement="center"
              size={[0, 0]}
            >
              <div
                key={isAnimated ? `anim-${cluster.id}` : undefined}
                className="counter-rotate"
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  animation: isAnimated
                    ? 'markerPopIn 0.5s cubic-bezier(0.23, 1, 0.32, 1)'
                    : undefined
                }}
              >
                {tourConfig?.marker ? (
                  <div
                    className={`tour-cluster tour-cluster-${cluster.id}`}
                    data-cluster-id={cluster.id}
                    style={{
                      position: 'absolute',
                      top: `${clusterTopOffset}px`,
                      width: `${clusterSize + 5}px`,
                      height: `${clusterSize + 5}px`,
                      cursor: 'pointer',
                      zIndex: 100000000,
                    }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClusterClick(cluster); }}
                    onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleClusterClick(cluster); }}
                  >
                    <img
                      src={tourConfig.marker}
                      alt=""
                      aria-hidden="true"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    <span
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: clusterFontColor,
                        fontSize: '14px',
                        fontWeight: 'bold',
                        textShadow: '0px 0px 3px rgba(0,0,0,0.8)',
                      }}
                    >
                      {cluster.properties.point_count}
                    </span>
                  </div>
                ) : (
                  <div
                    className={`tour-cluster tour-cluster-${cluster.id}`}
                    data-cluster-id={cluster.id}
                    style={{
                      position: 'absolute',
                      top: `${clusterTopOffset}px`,
                      borderRadius: '50%',
                      minWidth: `${clusterSize}px`,
                      height: `${clusterSize}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      zIndex: 100000000,
                      ...clusterBorderStyle,
                    }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClusterClick(cluster); }}
                    onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleClusterClick(cluster); }}
                  >
                    {cluster.properties.point_count}
                  </div>
                )}
              </div>
            </Marker>
          );
        })}
        {/* Individual markers */}
        {individualMarkers.map((marker, index) => {
          const stop = marker.properties.stopData;
          const demoLimit = typeof tourConfig?.demo_stop === 'number' ? (tourConfig.demo_stop as number) : 2;
          const isLockedByDemo = isDemo && (demoLimit > 0 ? stop.order >= demoLimit : false);
          const isMarkerAnimated = !hasRouteToStop && animatedMarkerIds.includes(stop.id);
          return (
            <Marker
              key={stop.id}
              position={{
                lat: marker.geometry.coordinates[1],
                lng: marker.geometry.coordinates[0],
              }}
              placement="center"
              size={[0, 0]}
            >
              <div
                key={isMarkerAnimated ? `anim-${stop.id}` : undefined}
                className="counter-rotate"
                style={{
                  zIndex: 1000,
                  cursor: 'pointer',
                  animation: isMarkerAnimated
                    ? 'markerPopIn 0.5s cubic-bezier(0.23, 1, 0.32, 1)'
                    : undefined
                }}>
                {(marker.properties.stopData.config?.marker || svgStopMarkers[stop.s_title]) && !isLockedByDemo ? (() => {
                  const svgStopConfig = svgStopMarkers;
                  const svgConf = svgStopConfig[stop.s_title];
                  let markerAnchor = stop.config?.markerAnchor || 'bottom';
                  let markerSrc = marker.properties.stopData.config?.marker;
                  let size: number;
                  if (svgConf) {
                    markerAnchor = svgConf.anchor;
                    markerSrc = svgConf.src;
                    size = svgConf.baseSize * Math.pow(2, currentZoom - 18);
                  } else {
                    const markerScale = stop.config?.markerScale || 1;
                    size = (getPinSize(currentZoom) + 3) * markerScale;
                  }
                  return (
                    <div
                      className={`tour-pin stop-id-${stop.id}`}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: stop.id === activeStopId ? 'pinBounce 1s ease-in-out infinite' : undefined,
                      }}
                    >
                      <img
                        src={markerSrc}
                        alt=""
                        aria-hidden="true"
                        style={{
                          width: size,
                          height: size,
                          objectFit: 'contain',
                          position: 'absolute',
                          ...(markerAnchor === 'center' ? {
                            top: -size / 2,
                            left: -size / 2,
                          } : {
                            top: -size,
                            left: -size / 2,
                          }),
                        }}
                      />
                    </div>
                  );
                })() : (
                  <TourPinV2
                    visited={visitedLocations?.[stop.id]}
                    active={stop.id === activeStopId}
                    disabled={isLockedByDemo}
                    pinStrokeColor={isLockedByDemo ? '#4a4a4a' : (stop?.config?.color || undefined)}
                    pinClassName={isLockedByDemo ? 'tour-pin-demo' : undefined}
                    size={getPinSize(currentZoom)}
                    isDesktop={isDesktop}
                    id={stop.id}
                  >
                    {isLockedByDemo ? (
                      <i className="fa-solid fa-lock scale-120" aria-hidden="true"></i>
                    ) : isPlannedRouteResults && plannedRouteStopOrder?.includes(stop.id) ? (
                      <span style={{ fontWeight: 700, fontSize: '13px', color: 'inherit' }}>
                        {plannedRouteStopOrder.indexOf(stop.id) + 1}
                      </span>
                    ) : (
                      getPinDrawer(stop, 0)
                    )}
                  </TourPinV2>
                )}
              </div>
            </Marker>
          );
        })}
        {/* User-added places (not in clusters) */}
        {(userAddedPlaces || [])
          .filter((place) => !clusteredUserPlaceIds.has(`user_${place.place_id}`))
          .filter((place) => !(dropPinStep === 'positioning' && dropPinTempPlaceId === place.place_id))
          .map((place) => (
            <Marker
              key={`user_${place.place_id}`}
              position={[place.geometry.location.lat, place.geometry.location.lng]}
              placement="center"
              size={[0, 0]}
            >
              <div
                className={`user-added-pin counter-rotate place_id-${place.place_id}`}
                style={{ zIndex: 1000, cursor: 'pointer' }}
              >
                <TourPinV2
                  visited={false}
                  active={false}
                  disabled={false}
                  pinStrokeColor="#48526B"
                  size={getPinSize(currentZoom)}
                  isDesktop={isDesktop}
                  id={`user_${place.place_id}`}
                >
                  <i className="fa-solid fa-user" style={{ fontSize: '14px' }} aria-hidden="true" />
                </TourPinV2>
              </div>
            </Marker>
          ))}
        {/* Excluded route stops (small circles when filtered) */}
        {excludedRouteStops.map((stop) => (
          <Marker
            key={`excluded-${stop.id}`}
            position={{
              lat: stop.stop_location.latitude,
              lng: stop.stop_location.longitude,
            }}
            placement="center"
            size={[0, 0]}
          >
            <div
              className="counter-rotate"
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: 'white',
                border: `2px solid ${tourStopColor}`,
                position: 'relative',
                top: -6,
                left: -6,
                boxShadow: `0 0 3px ${tourStopColor}`,
                pointerEvents: 'none',
              }}
            />
          </Marker>
        ))}
      </MarkerLayer>
    </Pane>
  );
});
